import { Loading } from '@components';
import { Api, authService } from '@services';
import { useEffect, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { connect } from 'react-redux';
import { useInfiniteQuery, useQuery } from 'react-query';

import '../../../../scss/_export.module.scss';
import '../Header.scss';

import SearchInput from '../../../../components/inputs/SearchInput/SearchInput';
import { appToggleApplication } from '../../../../redux';
import { appService } from '../../../../services';
import Icon from '../../../Icon/Icon';
import AppPill from './AppPill';
import { useAppSwitcherStore } from './UseAppSwitcherStore/useAppSwitcherStore';

const PAGE_SIZE = 2000;

function ApplicationSwitcher({ isOpen, anchorRef, closeDropdown, currentApp, toggleApp }) {
  const [scrollCloseTrigger, setScrollCloseTrigger] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // ZUSTAND STATE (global) — UI-only
  const isHidden = useAppSwitcherStore((s) => s.isHidden);
  const setHidden = useAppSwitcherStore((s) => s.setHidden);
  const searchTerm = useAppSwitcherStore((s) => s.searchTerm);
  const setSearchTerm = useAppSwitcherStore((s) => s.setSearchTerm);
  const defaultAppFromStore = useAppSwitcherStore((s) => s.currentDefaultApp);
  const setDefaultApp = useAppSwitcherStore((s) => s.setDefaultApp);
  const hydrateFromBE = useAppSwitcherStore((s) => s.hydrateFromBE);

  const {
    data: pinnedData,
    refetch: refetchPinned,
    isFetched: isPinnedFetched,
  } = useQuery(['pinned-apps'], () => appService.getAppsPaged(1, PAGE_SIZE, null, true), {
    enabled: isOpen,
  });

  // Split apps using BE flag
  const pinnedApps = pinnedData?.data ?? [];
  const isMaxPinned = pinnedApps.length >= 5;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage: hasMore,
    refetch,
  } = useInfiniteQuery(
    ['apps', searchTerm],
    async ({ pageParam = 1 }) => {
      const filter = searchTerm?.trim() || null;
      return appService.getAppsPaged(pageParam, PAGE_SIZE, filter, false);
    },
    {
      enabled: isOpen,
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.hasNextPage ? allPages.length + 1 : undefined;
      },
      staleTime: 10000,
    },
  );

  const allApps = (() => {
    const all = (data?.pages ?? []).flatMap((p) => p?.data ?? []);
    const map = new Map();

    all.forEach((a) => map.set(a.id, a));

    return Array.from(map.values());
  })();

  const refreshApps = () => {
    refetch();
    refetchPinned();
  };

  const pinApp = async (appId) => {
    if (isMaxPinned) return;

    try {
      await Api.put(`/v2/applications/${appId}/pin`);
      refreshApps();
    } catch (e) {
      console.error(e);
    }
  };

  const unpinApp = async (appId) => {
    try {
      await Api.delete(`/v2/applications/${appId}/pin`);
      refreshApps();
    } catch (e) {
      console.error(e);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setOpenMenuId(null);
    setScrollCloseTrigger((prev) => !prev);
    closeDropdown();
  };

  const toggleAndCloseApp = (app) => {
    toggleApp(app);
    handleClose();
  };

  // Infinite scroll
  const handleScroll = (e) => {
    setScrollCloseTrigger((prev) => !prev);
    setOpenMenuId(null);

    if (!hasMore || isFetchingNextPage) return;

    const el = e.target;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

    if (atBottom) fetchNextPage();
  };

  // If pinned is hidden but we have no pinned apps anymore, unhide
  useEffect(() => {
    if (!isOpen) return;
    if (!isPinnedFetched) return;

    if (pinnedApps.length === 0 && isHidden) {
      setHidden(false);
    }
  }, [isOpen, isPinnedFetched, pinnedApps.length, isHidden, setHidden]);

  // Make sure default app is consistent between user pref + app switcher
  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      try {
        const { data: user } = await authService.getMe();
        hydrateFromBE(user?.defaultApplication || null);
      } catch (e) {
        console.error('Failed to hydrate default app from BE', e);
      }
    })();
  }, [isOpen, hydrateFromBE]);

  if (!isOpen) return null;

  return (
    <Overlay
      target={anchorRef?.current}
      show={isOpen}
      placement="bottom"
      rootClose
      onHide={handleClose}
    >
      {(props) => (
        <Popover {...props} className="application-popover">
          <Popover.Header className="d-flex justify-content-between align-items-center">
            <div>
              <span className="fw-semibold fs-5">Applications</span>
              <br />
              <span className="fs-sm info-text">Switch, Pin & Hide/Show Apps</span>
            </div>

            {pinnedApps.length > 0 && (
              <Icon
                containerClassName="app-pill-button py-0"
                name={isHidden ? 'view' : 'hide'}
                size="sm"
                variant="light"
                onClick={() => setHidden(!isHidden)}
                showPointer
                disabled={false}
              />
            )}
          </Popover.Header>

          <Popover.Body>
            <div className="application-switcher">
              {/* Pinned Apps */}
              {pinnedApps.length > 0 && (
                <div className={`pinned-section ${!isHidden ? 'pb-3' : ''}`}>
                  {pinnedApps.map((app) => (
                    <div key={app.id} className="pill-wrapper">
                      <AppPill
                        app={app}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        currentApp={currentApp}
                        currentDefaultApp={defaultAppFromStore}
                        toggleApp={toggleAndCloseApp}
                        closeDropdown={handleClose}
                        pinApp={() => pinApp(app.id)}
                        unpinApp={() => unpinApp(app.id)}
                        setDefaultApp={setDefaultApp}
                        section="pinned"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Search bar */}
              {!isHidden && (
                <div className="app-search-bar px-2 pb-3 sticky-top z-2">
                  <SearchInput
                    key={`app-search-${isOpen}-${searchTerm}`}
                    id="app-switcher-search"
                    initSearch={searchTerm || ''}
                    placeholder="Search applications..."
                    debounceTimeSec={0.5}
                    onChange={(val) => setSearchTerm(val)}
                  />
                </div>
              )}

              {/* Scrollable Section */}
              {!isHidden && (
                <div className="application-switcher-inner-list" onScroll={handleScroll}>
                  {isLoading ? (
                    <div className="apps-loading-state">
                      <Loading width={100} height={100} />
                    </div>
                  ) : allApps.length === 0 ? (
                    <div className="apps-empty-state text-center py-3">No applications found.</div>
                  ) : (
                    <>
                      {allApps.map((app) => (
                        <div key={app.id} className="pill-wrapper">
                          <AppPill
                            app={app}
                            openMenuId={openMenuId}
                            setOpenMenuId={setOpenMenuId}
                            currentApp={currentApp}
                            currentDefaultApp={defaultAppFromStore}
                            toggleApp={toggleAndCloseApp}
                            closeDropdown={handleClose}
                            onScrollClose={scrollCloseTrigger}
                            pinApp={() => pinApp(app.id)}
                            unpinApp={() => unpinApp(app.id)}
                            pinText={isMaxPinned ? '5 Max Pins' : 'Pin to Top'}
                            disablePin={isMaxPinned}
                            setDefaultApp={setDefaultApp}
                            section="list"
                          />
                        </div>
                      ))}

                      {isFetchingNextPage && (
                        <div className="apps-loading-more">
                          <Loading width={100} height={100} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </Popover.Body>
        </Popover>
      )}
    </Overlay>
  );
}

const mapStateToProps = ({ app }) => ({
  currentApp: app.app,
});

const mapDispatchToProps = (dispatch) => ({
  toggleApp: (app) => dispatch(appToggleApplication(app)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationSwitcher);
